/**
 * A reading board: a title strip pinned to the top of the pane, the document scrolling under it.
 *
 * The prose boards were pages -- a great cream headline, a rule under it, and everything
 * including the heading scrolling away together. That is right for something printed and wrong
 * for something with a rail down its side: three screens into the book the reader has nothing
 * on the glass saying which board they are on or how to get back, and the tool boards beside
 * them keep their toolbar pinned. So the heading moves onto the chrome, where a notation
 * program puts a document's name, and what the board is _about_ stays in the document.
 *
 * The strip is the only place a board's actions belong. Export's Save and Score's Export were
 * at the bottom of a long scroll, which is the one place a primary action cannot be reached
 * from; on the strip they are where the reader already looks for them.
 *
 * `measure` is the difference between prose, a table and a workbench. A measure exists so that
 * an eye can find the next line, and nothing with a column of figures in it is read that way --
 * a compass chart capped at a paragraph's width wastes half a window and wraps rows that should
 * not wrap. A board being worked in rather than read, like the editor, wants the whole window.
 *
 * The heading is drawn before the data arrives, so a board waiting on the pipeline is still
 * that board rather than one grey line. The caller passes `Loading` or `Failed` as children.
 */

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ACTION, LABEL } from "./Chrome";

export interface BoardProps {
  readonly kicker: ReactNode;
  readonly title: ReactNode;
  /** What the board is about, printed at the head of the document rather than on the chrome. */
  readonly lede?: ReactNode;
  readonly back?: { to: string; label: string };
  /** What can be done to what is on the board: buttons and links, styled with `ACTION`. */
  readonly actions?: ReactNode;
  /** How wide the document is allowed to get: prose is read, a table is scanned, a
   *  workbench is worked in. */
  readonly measure?: "prose" | "wide" | "full";
  readonly children: ReactNode;
}

/** What each measure costs the document in width and in margin. */
const WIDTH: Record<NonNullable<BoardProps["measure"]>, string> = {
  prose: "max-w-3xl px-5 md:px-8",
  wide: "max-w-6xl px-5 md:px-8",
  full: "px-3 md:px-4",
};

export default function Board({
  kicker,
  title,
  lede,
  back,
  actions,
  measure = "prose",
  children,
}: BoardProps): JSX.Element {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <header className="flex shrink-0 items-center gap-2.5 border-b border-shell-edge bg-shell-bar px-2.5 py-1.5">
        {back === undefined ? null : (
          <Link
            to={back.to}
            className={`${ACTION} gap-1.5 pl-1.5`}
            title={`Back to ${back.label.toLowerCase()}`}
          >
            <span aria-hidden="true">←</span>
            <span className="max-w-[9rem] truncate">{back.label}</span>
          </Link>
        )}
        <div className="min-w-0 flex-1">
          <div className={LABEL}>{kicker}</div>
          <h1 className="truncate text-[13px] font-medium leading-tight text-shell-lit">{title}</h1>
        </div>
        {actions === undefined ? null : (
          <div className="flex shrink-0 items-center gap-1">{actions}</div>
        )}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto bg-paper">
        <div className={`mx-auto w-full pb-16 pt-6 ${WIDTH[measure]}`}>
          {lede === undefined ? null : (
            <p className="mb-6 max-w-2xl text-sm leading-relaxed text-ink-faded">{lede}</p>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
